import React from 'react';

import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';

import { StyledButton } from './ButtonPrompt.style';
import { CLICKSTREAM_COMMON_PROPS } from '../utils/constants';

const styleProps = {
  variant: 'outlined',
  color: 'primary',
  size: 'medium',
  hoverScale: 1.01,
  tapScale: 0.95,
  loading: false,
  disabled: false
};

/**
 * A component for loading more content with smooth animations and loading states
 * @param {object} props - Component props
 * @param {boolean} props.loading - Whether the button is in loading state
 * @param {string} props.message - The text to display on the button
 * @param {string} props.messageType - The type of message to display on the button
 * @param {Function} props.onComponentClick - Function to call when button is clicked
 * @param {string} props.appearance - Button appearance style: 'primary' or 'secondary'
 * @param {string} props.instructionId - ID of the instruction group containing this button
 * @param {string} props.ctaUrl - URL to navigate to when button is clicked
 * @param {string} props.agentName - Optional agent name to include in the payload
 * @param props.shouldAnimate
 * @param props.threadUuid
 * @param props.onAnimationComplete
 * @returns {React.ReactElement} The Button component
 */
function ButtonPrompt({
  message,
  messageType = 'text',
  appearance = 'primary',
  onComponentClick,
  instructionId,
  ctaUrl,
  agentName,
  shouldAnimate,
  threadUuid,
  onAnimationComplete,
  ...props
}) {
  const {
    variant = 'outlined',
    color = 'primary',
    size = 'medium',
    hoverScale = 1.01,
    tapScale = 0.95,
    loading = false,
    disabled = false
  } = styleProps;

  const clickstreamProps = {
    ...CLICKSTREAM_COMMON_PROPS,
    elementName: 'prompt_chips',
    elementText: message,
    destination: ctaUrl
  };

  const handleClick = () => {
    if (ctaUrl) {
      window.open(ctaUrl, '_blank');
    } else if (onComponentClick) {
      const payload = {
        message,
        messageType,
        action: messageType === 'next' ? 'continue' : 'text',
        instructionId,
        agentName
      };
      onComponentClick({
        type: 'button_click',
        payload
      });
    }
  };

  const MotionButton = motion.create(StyledButton);
  // Ensure all MUI props are strings to avoid capitalize errors
  const safeAppearance = typeof appearance === 'string' ? appearance : 'primary';
  const safeVariant = typeof variant === 'string' ? variant : 'outlined';
  const safeColor = typeof color === 'string' ? color : 'primary';
  const safeSize = typeof size === 'string' ? size : 'medium';

  return (
    <Box display="flex" justifyContent="flex-end">
      <Box maxWidth="85%">
        <MotionButton
          appearance={safeAppearance}
          aria-label={loading ? `${message} - Loading` : message}
          clickstreamProps={clickstreamProps}
          color={safeColor}
          disabled={disabled || loading}
          loading={loading}
          size={safeSize}
          variant={safeVariant}
          whileHover={{ scale: hoverScale }}
          whileTap={{ scale: tapScale }}
          onClick={handleClick}
          {...props}
        >
          {message}
        </MotionButton>
      </Box>
    </Box>
  );
}

ButtonPrompt.propTypes = {
  message: PropTypes.string,
  messageType: PropTypes.string,
  onComponentClick: PropTypes.func,
  appearance: PropTypes.oneOf(['primary', 'secondary']),
  instructionId: PropTypes.string,
  ctaUrl: PropTypes.string,
  agentName: PropTypes.string,
  shouldAnimate: PropTypes.bool,
  threadUuid: PropTypes.string,
  onAnimationComplete: PropTypes.func
};

export default ButtonPrompt;
